import { scmUnsupported, useScmConnections } from '@/api/hooks';
import type { ScmConnection } from '@/api/schemas';
import { stagger } from '@/lib/motion';
import { EmptyState } from '@/components/EmptyState';
import { ErrorState } from '@/components/ErrorState';
import { GatedButton } from '@/components/GatedButton';
import { Icon } from '@/components/Icon';
import { Card, CardAction, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { ScmConnectionsList } from './ScmConnectionsList';
import { useScmConnectionActions, type ScmConnectionActions } from './useScmConnectionActions';

function ConnectionsEmpty() {
  return (
    <EmptyState
      compact
      testId="scm-connections-empty"
      icon="account_tree"
      title="No Bitbucket workspace yet"
      description="Connect the workspace your repositories are in with an access token. Hangar reads your source with it to build your apps: nothing to set up anywhere else."
    />
  );
}

function ConnectionsBody({ connections, actions }: { connections: ReturnType<typeof useScmConnections>; actions: ScmConnectionActions }) {
  const data: ScmConnection[] | undefined = connections.data;
  if (connections.isLoading && !data) return <Skeleton className="h-20 rounded-lg" aria-busy="true" />;
  if (connections.isError && !data) return <ErrorState compact error={connections.error} onRetry={() => void connections.refetch()} />;
  if (!data || data.length === 0) return <ConnectionsEmpty />;
  return <ScmConnectionsList connections={data} actions={actions} />;
}

/**
 * Integrations › Source control: the Bitbucket workspaces Hangar lists repositories from and, for native apps, uses to
 * create the Bitrise app and read the source Hangar gives its builds. Writes need `admin`.
 */
export function SourceControl() {
  const connections = useScmConnections();
  const actions = useScmConnectionActions();
  if (scmUnsupported(connections.error)) return null; // an API without source control: nothing to offer
  return (
    <Card className="enter" style={stagger(2)} data-testid="source-control">
      <CardHeader>
        <CardTitle>Source control{connections.data ? ` (${connections.data.length})` : ''}</CardTitle>
        <CardDescription>Bitbucket workspaces and repositories: the repository picker of Connect app, and the token Hangar reads your source with (it never leaves Hangar)</CardDescription>
        <CardAction>
          <GatedButton data-testid="scm-connection-add" can={actions.can} blockedReason={actions.blocked} onClick={actions.connect}>
            <Icon name="add" size={18} />
            Connect Bitbucket
          </GatedButton>
        </CardAction>
      </CardHeader>
      <CardContent>
        <ConnectionsBody connections={connections} actions={actions} />
      </CardContent>
      {actions.overlays}
    </Card>
  );
}
